<script lang="ts">
	import { marketsConfig } from '$lib/config/markets';
	import { userHoldingActiveItem } from '$lib/home/store';
	import {
		currentClosePosition,
		popupModalOpen,
		popupModalType,
		positionData
	} from '$lib/popups/popupsStore';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';
	import type { Position } from '$lib/types/position';

	import ProcessingText from '$lib/util/ProcessingText.svelte';
	import { addDollarSign, getNumBackInUnits, makeBodyUnscrollable } from '$lib/util/util';
	import BigNumber from 'bignumber.js';

	export let position: Position;
	export let isTpslUpdating = false;
	export let isCreating = false;

	function openTPSLModal() {
		$currentClosePosition = position;
		$popupModalType = 'TPSL';
		$popupModalOpen = true;
		$positionData = position;
		makeBodyUnscrollable();
	}

	const switchToOrdersTab = () => {
		userHoldingActiveItem.set('orders');
	};
</script>

<div class="flex justify-center items-center hidden sm:grid">
	{#if isTpslUpdating}
		<ProcessingText title="Updating" />
	{:else if !new BigNumber(position.takeProfit).gt(0) && !new BigNumber(position.stopLoss).gt(0)}
		{#if isCreating}
			-
		{:else}
			<Tooltip header="Entire Position TP/SL" title="Click to add an entire position TP/SL order">
				<button
					class="flex items-start justify-center px-3 py-1.5 cursor-pointer bg-fx-gradient-8 rounded-lg squircle-lg border border-fx-grey-500 text-sm text-fx-grey-200"
					on:click={openTPSLModal}
				>
					Add</button
				>
			</Tooltip>
		{/if}
	{:else}
		<div class="flex gap-2">
			<div class="flex items-center gap-2">
				<div class="flex flex-col items-center">
					<div>
						{#if new BigNumber(position.takeProfit).gt(0)}
							<div class="flex flex-col gap-1 items-start justify-center mx-auto">
								<div class="text-fx-gradient-23 text-xs font-medium text-center">
									{new BigNumber(position.takeProfit).gte(1e24)
										? '$' +
										  Number.parseFloat(
												getNumBackInUnits(
													position.takeProfit,
													18,
													marketsConfig[position.asset].showDecimals
												)
										  ).toExponential(2)
										: addDollarSign(
												getNumBackInUnits(
													position.takeProfit,
													18,
													marketsConfig[position.asset].showDecimals
												),
												marketsConfig[position.asset].showDecimals
										  )}
								</div>
							</div>
						{:else}
							<div class="text-fx-gradient-23">-</div>
						{/if}
					</div>
					<div>
						{#if new BigNumber(position.stopLoss).gt(0)}
							<div class="flex flex-col gap-1 items-start justify-center mx-auto">
								<div class="text-fx-gradient-22 text-xs font-medium text-center">
									{new BigNumber(position.stopLoss).gte(1e24)
										? '$' +
										  Number.parseFloat(
												getNumBackInUnits(
													position.stopLoss,
													18,
													marketsConfig[position.asset].showDecimals
												)
										  ).toExponential(2)
										: addDollarSign(
												getNumBackInUnits(
													position.stopLoss,
													18,
													marketsConfig[position.asset].showDecimals
												),
												marketsConfig[position.asset].showDecimals
										  )}
								</div>
							</div>
						{:else}
							<div class="text-fx-gradient-22">-</div>
						{/if}
					</div>
				</div>
				<div>
					<Tooltip title="Edit" header="Edit TP/SL order">
						<div
							class="h-full"
							on:click|stopPropagation={openTPSLModal}
							on:keydown|stopPropagation={openTPSLModal}
						>
							<img class="w-4" src="edit-icon.svg" alt="edit" />
						</div>
					</Tooltip>
					{#if position.noOfTpsl > 1}
						<Tooltip
							header="{position.noOfTpsl - 1} More TP/SL order"
							title="View all TP/SL orders associated with this position"
						>
							<button
								class="text-fx-white text-xs text-center cursor-pointer underline underline-offset-2"
								on:click|stopPropagation={switchToOrdersTab}
							>
								(+{position.noOfTpsl - 1})
							</button>
						</Tooltip>
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>

<div class="flex sm:hidden block">
	{#if isTpslUpdating}
		<ProcessingText title="Updating" />
	{:else if !new BigNumber(position.takeProfit).gt(0) && !new BigNumber(position.stopLoss).gt(0)}
		{#if isCreating}
			-
		{:else}
			<button
				class="flex items-start justify-center px-3 py-1.5 cursor-pointer bg-fx-gradient-8 rounded-lg squircle-lg border border-fx-grey-500 text-sm text-fx-grey-200 mt-2"
				on:click={openTPSLModal}
			>
				Add</button
			>
		{/if}
	{:else}
		<div class="flex gap-2">
			<div class="flex items-center gap-2">
				<div class="flex items-center gap-1">
					<div>
						{#if new BigNumber(position.takeProfit).gt(0)}
							<div class="flex flex-col gap-1 items-start justify-center mx-auto">
								<div class="text-fx-gradient-23 text-sm font-medium text-center">
									{new BigNumber(position.takeProfit).gte(1e24)
										? '$' +
										  Number.parseFloat(
												getNumBackInUnits(
													position.takeProfit,
													18,
													marketsConfig[position.asset].showDecimals
												)
										  ).toExponential(2)
										: addDollarSign(
												getNumBackInUnits(
													position.takeProfit,
													18,
													marketsConfig[position.asset].showDecimals
												),
												marketsConfig[position.asset].showDecimals
										  )}
								</div>
							</div>
						{:else}
							<div class="text-fx-gradient-23">-</div>
						{/if}
					</div>
					<div class="text-sm font-medium">/</div>
					<div>
						{#if new BigNumber(position.stopLoss).gt(0)}
							<div class="flex flex-col gap-1 items-start justify-center mx-auto">
								<div class="text-fx-gradient-22 text-sm font-medium text-center">
									{new BigNumber(position.stopLoss).gte(1e24)
										? '$' +
										  Number.parseFloat(
												getNumBackInUnits(
													position.stopLoss,
													18,
													marketsConfig[position.asset].showDecimals
												)
										  ).toExponential(2)
										: addDollarSign(
												getNumBackInUnits(
													position.stopLoss,
													18,
													marketsConfig[position.asset].showDecimals
												),
												marketsConfig[position.asset].showDecimals
										  )}
								</div>
							</div>
						{:else}
							<div class="text-fx-gradient-22">-</div>
						{/if}
					</div>
				</div>
				<div class="flex gap-2">
					<div
						class="h-4 w-4"
						on:click|stopPropagation={openTPSLModal}
						on:keydown|stopPropagation={openTPSLModal}
					>
						<img class="w-4" src="edit-icon.svg" alt="edit" />
					</div>
					{#if position.noOfTpsl > 1}
						<button
							class="text-fx-white text-xs text-center cursor-pointer underline underline-offset-2"
							on:click|stopPropagation={switchToOrdersTab}
							on:keydown|stopPropagation={switchToOrdersTab}
						>
							(+{position.noOfTpsl - 1})
						</button>
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>
